import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { useFiltersPanel } from '~/gridv2/context/FiltersPanelContext';
import WarningIcon from '~/icons/components/WarningIcon';
import { useBreakpoint } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

const WarningIconWrapper = styled.div`
  display: flex;
  width: 32px;
  height: 32px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 64px;
  background: ${({ theme }) => theme.colors.gridV2NoResultsFoundIconBackground};
`;

const Wrapper = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 32px;
  gap: 12px;
  ${({ $isMobile }) =>
    !$isMobile &&
    `
    border-right: 1px solid ${({ theme }) =>
      theme.colors.gridV2NoResultsFoundBorder};
    border-left: 1px solid ${({ theme }) =>
      theme.colors.gridV2NoResultsFoundBorder};
  `}
  border-bottom: 1px solid ${({ theme }) =>
    theme.colors.gridV2NoResultsFoundBorder};
  border-top: 1px solid
    ${({ theme }) => theme.colors.gridV2NoResultsFoundBorder};
  background: ${({ theme }) => theme.colors.gridV2NoResultsFoundBackground};
  height: ${({ $isMobile }) => ($isMobile ? '300px' : '396px')};
`;

const NoResultsFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const TextAndButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const TitleAndText = styled.div`
  display: flex;
  width: 265px;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const Title = styled.div`
  align-self: stretch;
  color: ${({ theme }) => theme.colors.gridV2NoResultsFoundText};
  text-align: center;
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px; /* 116.667% */
`;

const Text = styled.div`
  align-self: stretch;
  color: ${({ theme }) => theme.colors.gridV2NoResultsFoundText};
  text-align: center;
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`;

const FiltersButton = styled.button`
  display: flex;
  height: 48px;
  padding: 0 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  background: ${({ theme }) =>
    theme.colors.gridV2NoResultsFoundFilterButtonBackground};
  color: ${({ theme }) => theme.colors.gridV2NoResultsFoundFilterButtonText};
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px; /* 100% */
  text-transform: uppercase;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;

const NoResultsFound = ({
  description,
  title,
  buttonLabel,
  setIsFiltersPaneOpen,
}) => {
  const { isMobile } = useBreakpoint();
  const { openFiltersPanel } = useFiltersPanel();
  // TODO: distinguish between no results due to filters (multi-applied via mobile) vs no results due to invalid search query

  return (
    <Wrapper $isMobile={isMobile} data-autotag="grid-empty-msg">
      <NoResultsFoundContainer>
        <WarningIconWrapper>
          <WarningIcon />
        </WarningIconWrapper>
        <TextAndButton>
          <TitleAndText>
            <Title>
              {title || (
                <FormattedMessage
                  id="grid_static.no_results_found_title"
                  defaultMessage="NO RESULTS FOUND"
                />
              )}
            </Title>
            <Text>
              {description || (
                <FormattedMessage
                  id="grid_static.no_results_found_description"
                  defaultMessage="Uh-oh! We couldn't find any results. Try adjusting your filters."
                />
              )}
            </Text>
          </TitleAndText>
          <FiltersButton
            data-autotag="filter-change-cta"
            onClick={
              // setIsFiltersPaneOpen works for mobile, is not defined for desktop
              // openFiltersPanel works for desktop, does not work for mobile
              setIsFiltersPaneOpen
                ? () => setIsFiltersPaneOpen(true)
                : openFiltersPanel
            }
          >
            {buttonLabel || (
              <FormattedMessage
                id="grid_static.no_results_found_cta_label"
                defaultMessage="Edit Filters"
              />
            )}
          </FiltersButton>
        </TextAndButton>
      </NoResultsFoundContainer>
    </Wrapper>
  );
};

NoResultsFound.propTypes = {
  buttonLabel: PropTypes.string,
  description: PropTypes.string,
  setIsFiltersPaneOpen: PropTypes.func,
  title: PropTypes.string,
};

export default NoResultsFound;
